import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@qapline/helpers';
import { ColumnFiltersState, PaginationState, RowSelectionState, SortingState } from '@tanstack/table-core';
import { isEqual } from 'lodash';

import {
    DEFAULT_PAGE_SIZE_OPTIONS,
    TABLE_COLUMN_ORDER_STORAGE_KEY_PREFIX,
    TABLE_COLUMN_SIZES_STORAGE_KEY_PREFIX,
    TABLE_COLUMN_VISIBILITY_STORAGE_KEY_PREFIX,
    TABLE_PAGE_SIZE_STORAGE_KEY_PREFIX,
} from '../types';
import { getStoredColumnSizes } from '../utils';
import { getStoredColumnOrder, getStoredColumnVisibility } from '../utils/getStoredColumnLayout';
import { useStoredTableState } from './useStoredTableState';

export type TUseTableStateArgs = {
    pageSizeOptions?: number[];
    storeColumnSizes?: string;
    storeColumnLayout?: string;
    storePageSize?: string;
    paginateByQuery?: boolean;
    initialSorting?: SortingState;
    initialColumnFilters?: ColumnFiltersState;
};

export const useTableState = ({
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    storeColumnSizes,
    storeColumnLayout,
    storePageSize,
    paginateByQuery,
    initialColumnFilters,
    initialSorting,
}: TUseTableStateArgs) => {
    const query = useQuery();
    const navigate = useNavigate();

    const getInitialPaginationState = () => {
        const queryPageSize = query.get('pageSize') ? Number(query.get('pageSize')) : undefined;
        const storedPageSize = storePageSize
            ? Number(localStorage.getItem(`${TABLE_PAGE_SIZE_STORAGE_KEY_PREFIX}${storePageSize}`))
            : undefined;
        const pageSize = [queryPageSize, storedPageSize].find(
            (value): value is number => value !== undefined && pageSizeOptions.includes(value),
        );

        if (!paginateByQuery) {
            return {
                pageIndex: 0,
                pageSize: pageSize ?? pageSizeOptions[0],
            };
        }
        return {
            pageIndex: query.get('page') ? Number(query.get('page')) : 0,
            pageSize: pageSize ?? pageSizeOptions[0],
        };
    };

    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters ?? []);
    const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [pagination, setPagination] = useState<PaginationState>(getInitialPaginationState());
    const [columnSizing, setColumnSizing] = useStoredTableState(
        TABLE_COLUMN_SIZES_STORAGE_KEY_PREFIX,
        storeColumnSizes,
        getStoredColumnSizes,
    );
    const [columnVisibility, setColumnVisibility] = useStoredTableState(
        TABLE_COLUMN_VISIBILITY_STORAGE_KEY_PREFIX,
        storeColumnLayout,
        getStoredColumnVisibility,
    );
    const [columnOrder, setColumnOrder] = useStoredTableState(
        TABLE_COLUMN_ORDER_STORAGE_KEY_PREFIX,
        storeColumnLayout,
        getStoredColumnOrder,
    );
    const columnFiltersRef = useRef(columnFilters);
    columnFiltersRef.current = columnFilters;

    const handleSetPagination: Dispatch<SetStateAction<PaginationState>> = useCallback(
        (action) => {
            setPagination((prevState) => {
                const newState = typeof action === 'function' ? action(prevState) : action;

                if (storePageSize && newState.pageSize !== prevState.pageSize) {
                    localStorage.setItem(
                        `${TABLE_PAGE_SIZE_STORAGE_KEY_PREFIX}${storePageSize}`,
                        String(newState.pageSize),
                    );
                }

                //Update query params if paginateByQueryProp is set
                if (paginateByQuery) {
                    query.set('page', String(newState.pageIndex));
                    query.set('pageSize', String(newState.pageSize));
                    navigate(`?${query.toString()}`, { relative: 'path' });
                }

                return newState;
            });
        },
        [setPagination, paginateByQuery, query, navigate, storePageSize],
    );

    const resetPageIndex = useCallback(() => {
        if (pagination.pageIndex !== 0) {
            handleSetPagination((prevState) => ({ ...prevState, pageIndex: 0 }));
        }
    }, [handleSetPagination, pagination.pageIndex]);

    const handleSetSearch = useCallback(
        (value: string) => {
            setSearch(value);
            // A new search term produces a different result set, start it from the first page
            resetPageIndex();
        },
        [resetPageIndex],
    );

    const handleSetColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>> = useCallback(
        (action) => {
            const prevState = columnFiltersRef.current;
            const newState = typeof action === 'function' ? action(prevState) : action;
            if (isEqual(newState, prevState)) return;

            columnFiltersRef.current = newState;
            setColumnFilters(newState);
            resetPageIndex();
        },
        [resetPageIndex],
    );

    const handleSetSorting: Dispatch<SetStateAction<SortingState>> = useCallback(
        (action) => {
            setSorting((prevState) => {
                const newState = typeof action === 'function' ? action(prevState) : action;
                if (initialSorting && initialSorting.length > 0 && newState.length === 0) {
                    return initialSorting;
                }
                return newState;
            });
        },
        [initialSorting],
    );

    return {
        // States
        search,
        columnFilters,
        sorting,
        rowSelection,
        pagination,
        columnSizing,
        columnVisibility,
        columnOrder,

        // Setters
        setSearch: handleSetSearch,
        setSorting: handleSetSorting,
        setRowSelection,
        setColumnFilters: handleSetColumnFilters,
        setPagination: handleSetPagination,
        setColumnSizing,
        setColumnVisibility,
        setColumnOrder,
    };
};
